import React, {
  useCallback, useEffect, useState, useMemo, useContext, Fragment,
} from 'react';
import './OrderHistory.scss';
import { useDispatch, useSelector } from 'react-redux';
import { unwrapResult } from '@reduxjs/toolkit';
import _ from 'lodash';
import {
  getPairMatchedOrderByAddress,
  getPairOrderHistoryByAddress,
} from 'store/exchange/order/orderThunk';
import { currentNetworkId, selectNetworkById } from 'store/exchange/network/networksSlice';
import { accountAddress } from 'store/exchange/account/accountSlice';
import ExtensionListener, { requestSignature, requestTransaction } from 'view/common/listener/ExtensionListener';
import { sendSignatureRequest } from 'view/common/listener/SignatureListener';
import { formatDigits } from 'view/router/exchange/util/numberUtil';
import { MAX_DIGITS, SIDE_BUY, SIDE_SELL } from 'view/router/exchange/util/literal';
import {
  AutoSizer, InfiniteLoader, List,
} from 'react-virtualized';
import { useErrorHandler } from 'react-error-boundary';
import toast from 'react-hot-toast';
import HistoryOrdersRow from './HistoryOrderRow';
import ActiveOrdersRow from './ActiveOrderRow';
import { TradeContext } from '../../context/TradeContext';

const ORDER_LIMIT = 10;

function OrderHistory() {
  const {
    pair, tradeHistory, setTradeHistory, activeOrder, setActiveOrder,
    setTradable,
  } = useContext(TradeContext);

  const {
    symbol, market, id: pairId, cancelSymbolFee, cancelMarketFee,
  } = pair || {};
  const handleError = useErrorHandler();

  const dispatch = useDispatch();

  const reduxAccountAddress = useSelector(accountAddress);
  const networkId = useSelector(currentNetworkId);
  const reduxSelectedNetwork = useSelector(
    (state) => selectNetworkById(state, networkId),
  );

  const [isOrderTab, setIsOrderTab] = useState(true);

  const {
    chain_id: chainId,
    chain_network_id: chainNetworkId,
    micro_chain_id: microChainId,
    contract_address: contractAddress,
  } = reduxSelectedNetwork || {};

  const [nextDataTradeHistory, setNextDataTradeHistory] = useState(false);

  const tradeHistoryLastId = useMemo(
    () => _.head(_.takeRight(tradeHistory))?.id,
    [tradeHistory],
  );

  const setTradeHistoryToState = useCallback(async (lastId) => {
    const result = unwrapResult(
      await dispatch(
        getPairMatchedOrderByAddress({
          ...(lastId && { lastId }),
          address: reduxAccountAddress,
          limit: ORDER_LIMIT,
          pairId,
        }),
      ),
    );
    setNextDataTradeHistory(result.length === ORDER_LIMIT);
    if (result.length > 0) {
      setTradeHistory(lastId ? tradeHistory.concat(result) : result);
    }
  }, [tradeHistory, tradeHistoryLastId, pairId, reduxAccountAddress]);

  const [nextDataActiveOrder, setNextDataActiveOrder] = useState(true);

  const activeOrderLastId = useMemo(
    () => _.head(_.takeRight(activeOrder))?.id,
    [activeOrder],
  );

  const setActiveOrderToState = useCallback(async (lastId) => {
    const result = unwrapResult(
      await dispatch(
        getPairOrderHistoryByAddress({
          address: reduxAccountAddress,
          ...(lastId && { lastId }),
          limit: ORDER_LIMIT,
          pairId,
        }),
      ),
    );
    setNextDataActiveOrder(result.length === ORDER_LIMIT);
    if (result.length > 0) {
      setActiveOrder(_.uniqBy(lastId ? activeOrder.concat(result) : result, 'order_id'));
    }
  }, [activeOrder, activeOrderLastId, pairId, reduxAccountAddress]);

  useEffect(() => {
    if (networkId && pairId && reduxAccountAddress !== '' && reduxAccountAddress) {
      setTradeHistoryToState()
        .catch((e) => {
          // console.error('[OrderHistory]:: getPairMatchedOrderByAddress', e);
          toast('something wrong during getting your trade history... please try again', { icon: '❌' });
          setTradable(false);
        });
      setActiveOrderToState()
        .catch((e) => {
          // console.error('[OrderHistory]:: getPairOrderHistoryByAddress', e);
          toast('something wrong during getting your active orders... please try again', { icon: '❌' });
          setTradable(false);
        });
    }
    return () => {
      setActiveOrder([]);
      setTradeHistory([]);
      setNextDataTradeHistory(true);
      setNextDataActiveOrder(true);
    };
  }, [networkId, reduxAccountAddress, pairId]);

  const handleNewPageLoadForTradeHistory = useCallback(
    async () => {
      try {
        await setTradeHistoryToState(tradeHistoryLastId);
      } catch (e) {
        // console.error('[OrderHistory]::setTradeHistoryToState', e);
        toast('something wrong during getting your trade history... please try again', { icon: '❌' });
        setTradable(false);
      }
    },
    [setTradeHistoryToState, tradeHistoryLastId, nextDataTradeHistory],
  );

  const handleNewPageLoadForActiveOrder = useCallback(
    async () => {
      try {
        await setActiveOrderToState(activeOrderLastId);
      } catch (e) {
        // console.error('[OrderHistory]::setActiveOrderToState', e);
        toast('something wrong during getting your active orders... please try again', { icon: '❌' });
        setTradable(false);
      }
    },
    [setActiveOrderToState, activeOrderLastId, nextDataActiveOrder],
  );

  const [requestInfo, setRequestInfo] = useState(null);

  const orderCancelBtnHandler = useCallback(async (history) => {
    const payload = {
      pair: `${symbol}_${market}`,
      order_id: history.order_id,
      side: history.side === SIDE_BUY ? 'buy' : 'sell',
      price: history.price,
      amount: history.origin_amount,
    };
    const loginType = sessionStorage.getItem('loginType');
    if (loginType * 1 === 1) {
      requestSignature({
        id: 'CancelOrder',
        ...payload,
      });
      setRequestInfo({ ...payload });
    } else {
      sendSignatureRequest({
        network_id: networkId,
        chain_network_id: chainNetworkId,
        chain_id: chainId,
        micro_chain_id: microChainId,
        address: reduxAccountAddress,
        contract_address: contractAddress,
        function_name: 'CancelOrder',
        tx_data: { ...payload },
        data_need_signature: true,
      });
    }
  }, [networkId, reduxSelectedNetwork, reduxAccountAddress, symbol, market]);

  const requestTransactionToExtensionAfterSignature = useCallback((payload) => {
    const { id, ...rest } = payload;
    if (id === 'CancelOrder') {
      requestTransaction({
        networkId,
        chainNetworkId,
        chainId,
        microChainId,
        address: reduxAccountAddress,
        contractAddressQuery: contractAddress,
        nonceDomain: `${process.env.REACT_APP_EXCHANGE_SERVER_URL}${process.env.REACT_APP_API_VERSION}dex/request/nonce`,
        transactionDomain: `${process.env.REACT_APP_EXCHANGE_SERVER_URL}${process.env.REACT_APP_API_VERSION}dex/order/cancel`,
        functionName: 'CancelOrder',
        tx_data: { ...requestInfo, ...rest },
      });
    }
  }, [networkId, reduxSelectedNetwork, reduxAccountAddress, symbol, market, requestInfo]);

  const noAddress = useMemo(() => !reduxAccountAddress, [reduxAccountAddress]);
  const noData = useMemo(
    () => Boolean(!!reduxAccountAddress && (isOrderTab
      ? activeOrder.length === 0 : tradeHistory.length === 0)),
    [isOrderTab, activeOrder, tradeHistory, reduxAccountAddress],
  );

  const isActiveOrderRowLoaded = useCallback(({ index }) => !!activeOrder[index], [activeOrder]);

  const isTradeHistoryRowLoaded = useCallback(({ index }) => !!tradeHistory[index], [tradeHistory]);

  const loadMoreActiveOrderRows = useCallback(async ({ startIndex, stopIndex }) => {
    if (nextDataActiveOrder) {
      await handleNewPageLoadForActiveOrder();
    }
  }, [nextDataActiveOrder, handleNewPageLoadForActiveOrder]);

  const loadMoreTradeHistoryRows = useCallback(async ({ startIndex, stopIndex }) => {
    if (nextDataTradeHistory) {
      await handleNewPageLoadForTradeHistory();
    }
  }, [nextDataTradeHistory, handleNewPageLoadForTradeHistory]);

  const inputHandler = useCallback(
    (e) => {
      setIsOrderTab(Boolean(e.target.value * 1));
    },
    [setIsOrderTab],
  );

  return (
    <div id="order-history">
      <ExtensionListener setSignature={requestTransactionToExtensionAfterSignature} />
      <div className="header-wrapper">
        <label>
          <input type={'radio'}
                 name='order-history'
                 value={1}
                 checked={isOrderTab}
                 onChange={inputHandler}
          />
          <span>Active Orders</span>
        </label>
        <label>
          <input type={'radio'}
                 name='order-history'
                 value={0}
                 checked={!isOrderTab}
                 onChange={inputHandler}
          />
          <span>Trade History</span>
        </label>
      </div>
      <div className="content-wrapper">
        <div className="table-wrapper" style={{ display: isOrderTab ? 'block' : 'none' }}>
          <div style={{
              textAlign: 'right',
              fontSize: 12,
              backgroundColor: '#222222',
              color: '#e5e5e5',
              paddingRight: 15,
              paddingTop: 5,
              paddingBottom: 5,
            }}>
            {
              `cancel fee : ${formatDigits(cancelSymbolFee, MAX_DIGITS, 0)} ${symbol} for selling / 
              ${formatDigits(cancelMarketFee, MAX_DIGITS, 0)} ${market} for buying`
            }
          </div>
          <AutoSizer>
            {
              ({ width, height }) => (
                <InfiniteLoader
                  isRowLoaded={isActiveOrderRowLoaded}
                  loadMoreRows={loadMoreActiveOrderRows}
                  minimumBatchSize={ORDER_LIMIT}
                  rowCount={1000}
                  threshold={Math.floor(ORDER_LIMIT * 0.2)}
                >
                  {({ onRowsRendered, registerChild }) => (
                    <List
                      width={width}
                      height={height - 24}
                      onRowsRendered={onRowsRendered}
                      rowHeight={92}
                      rowCount={activeOrder.length}
                      ref={registerChild}
                      rowRenderer={({ key, index, style }) => <ActiveOrdersRow
                        key={key}
                        style={style}
                        item={activeOrder[index]}
                        orderCancelBtnHandler={orderCancelBtnHandler}
                      />}
                      noRowsRenderer={() => <Fragment>
                          {
                            noData
                            && <div className="no-data">No Active Orders</div>
                          }
                          {noAddress && (
                            <div className="no-data no-login">
                              Connect wallet and check your active orders.
                            </div>
                          )}
                        </Fragment>
                      }
                    />
                  )}
                </InfiniteLoader>
              )}
          </AutoSizer>
        </div>
        <div className="table-wrapper" style={{ display: isOrderTab ? 'none' : 'block' }}>
          <AutoSizer>
            {
              ({ width, height }) => (
                <InfiniteLoader
                  isRowLoaded={isTradeHistoryRowLoaded}
                  loadMoreRows={loadMoreTradeHistoryRows}
                  minimumBatchSize={ORDER_LIMIT}
                  rowCount={1000}
                  threshold={Math.floor(ORDER_LIMIT * 0.2)}
                >
                  {({ onRowsRendered, registerChild }) => (
                    <List
                      width={width}
                      height={height}
                      onRowsRendered={onRowsRendered}
                      rowHeight={92}
                      rowCount={tradeHistory.length}
                      ref={registerChild}
                      rowRenderer={({ key, index, style }) => <HistoryOrdersRow
                          key={key}
                          style={style}
                          item={tradeHistory[index]}
                        />
                      }
                      noRowsRenderer={() => <Fragment>
                        {
                          noData
                          && <div className="no-data">No Trade History</div>
                        }
                        {noAddress && (
                          <div className="no-data no-login">
                            Connect wallet and check your trade history.
                          </div>
                        )}
                      </Fragment>
                      }
                    />
                  )}
                </InfiniteLoader>
              )}
          </AutoSizer>
        </div>
      </div>
    </div>
  );
}

export default OrderHistory;
